"use client";

import {
	Area,
	AreaChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { ChartTooltip } from "../../components/chart-tooltip";
import type { ChartDataItem } from "../../utils/chart-values";
import { womacChartConfig } from "./utils";

interface WomacChartProps {
	chartData: ChartDataItem[];
}

export function WomacChart({ chartData }: WomacChartProps) {
	console.log("WOMAC Chart Data", chartData);
	// Filter out data points with no entries
	const filteredData = chartData.filter((point) => point.entries_count > 0);

	const formatPercentage = (value: number, max: number) => {
		return `${((value / max) * 100).toFixed(1)}%`;
	};

	const formatScore = (value: number) => {
		return value.toFixed(1);
	};

	return (
		<ResponsiveContainer width="100%" height={400}>
			<AreaChart
				data={filteredData}
				margin={{
					top: 10,
					right: 30,
					left: 0,
					bottom: 0,
				}}
			>
				<CartesianGrid strokeDasharray="3 3" />
				<XAxis
					dataKey="timing"
					tick={{ fill: "hsl(var(--foreground))" }}
					axisLine={{ stroke: "hsl(var(--border))" }}
					tickLine={{ stroke: "hsl(var(--border))" }}
				/>
				<YAxis
					tick={{ fill: "hsl(var(--foreground))" }}
					axisLine={{ stroke: "hsl(var(--border))" }}
					tickLine={{ stroke: "hsl(var(--border))" }}
					domain={[0, 96]}
				/>
				<Tooltip
					content={({ active, payload }) => {
						if (!active || !payload?.length || !payload[0]?.payload)
							return null;
						const data = payload[0].payload;

						const totalScore = formatScore(
							data["WOMAC Function Score"] +
								data["WOMAC Pain Score"] +
								data["WOMAC Stiffness Score"],
						);

						return (
							<div className="bg-white shadow-lg p-3 border rounded-lg w-[200px] md:w-[300px]">
								<div className="flex flex-col gap-2">
									<div className="flex flex-col">
										<span className="mb-1 text-[0.65rem] text-slate-400 uppercase tracking-wider">
											Timing
										</span>
										<span className="font-medium text-slate-700 text-sm">
											{data.timing}
										</span>
										<div className="mt-1 text-slate-600 text-xs">
											{data.entries_count} entries
										</div>
									</div>

									<div className="flex flex-col bg-slate-50 shadow-sm p-2 border border-slate-200 rounded-md">
										<span className="mb-1 font-bold text-[0.65rem] text-slate-500 uppercase tracking-wider">
											WOMAC Osteoarthritis Index
										</span>
										<span className="flex items-center font-semibold text-slate-900 text-lg">
											<span className="text-indigo-600">{totalScore}/96</span>
											<span className="px-2 text-slate-500 text-sm">=</span>
											<span className="bg-indigo-100 px-2 py-0.5 rounded-full text-slate-600 text-sm">
												{formatPercentage(
													data["WOMAC Function Score"] +
														data["WOMAC Pain Score"] +
														data["WOMAC Stiffness Score"],
													96,
												)}
											</span>
										</span>
									</div>
									<div className="flex flex-col gap-1.5 pt-1 border-slate-100 border-t">
										<div className="flex flex-col">
											<span className="mb-1 text-[0.65rem] text-slate-600 uppercase tracking-wider">
												Pain Score
											</span>
											<span className="font-medium text-slate-700 text-sm">
												<span className="bg-[hsl(var(--chart-4))]/10 px-1.5 py-0.5 rounded text-slate-900">
													{formatScore(data["WOMAC Pain Score"])}/20
												</span>
												<span className="mx-1 text-slate-500">=</span>
												<span className="px-1.5 py-0.5 border border-[hsl(var(--chart-4))] rounded text-slate-900">
													{formatPercentage(data["WOMAC Pain Score"], 20)}
												</span>
											</span>
										</div>
										<div className="flex flex-col">
											<span className="mb-1 text-[0.65rem] text-slate-600 uppercase tracking-wider">
												Stiffness Score
											</span>
											<span className="font-medium text-slate-700 text-sm">
												<span className="bg-[hsl(var(--chart-1))]/10 px-1.5 py-0.5 rounded text-slate-900">
													{formatScore(data["WOMAC Stiffness Score"])}/8
												</span>
												<span className="mx-1 text-slate-500">=</span>
												<span className="px-1.5 py-0.5 border border-[hsl(var(--chart-1))] rounded text-slate-900">
													{formatPercentage(data["WOMAC Stiffness Score"], 8)}
												</span>
											</span>
										</div>
										<div className="flex flex-col">
											<span className="mb-1 text-[0.65rem] text-slate-600 uppercase tracking-wider">
												Function Score
											</span>
											<span className="font-medium text-slate-700 text-sm">
												<span className="bg-[hsl(var(--chart-2))]/10 px-1.5 py-0.5 rounded text-slate-900">
													{formatScore(data["WOMAC Function Score"])}/68
												</span>
												<span className="mx-1 text-slate-500">=</span>
												<span className="px-1.5 py-0.5 border border-[hsl(var(--chart-2))] rounded text-slate-900">
													{formatPercentage(data["WOMAC Function Score"], 68)}
												</span>
											</span>
										</div>
									</div>
								</div>
							</div>
						);
					}}
					cursor={false}
				/>
				<Area
					type="natural"
					dataKey="WOMAC Stiffness Score"
					stroke={womacChartConfig["WOMAC Stiffness Score"].color}
					fill={womacChartConfig["WOMAC Stiffness Score"].color}
					stackId="womac"
					strokeWidth={2}
				/>
				<Area
					type="natural"
					dataKey="WOMAC Function Score"
					stroke={womacChartConfig["WOMAC Function Score"].color}
					fill={womacChartConfig["WOMAC Function Score"].color}
					stackId="womac"
					strokeWidth={2}
				/>
				<Area
					type="natural"
					dataKey="WOMAC Pain Score"
					stroke={womacChartConfig["WOMAC Pain Score"].color}
					fill={womacChartConfig["WOMAC Pain Score"].color}
					stackId="womac"
					strokeWidth={2}
				/>
			</AreaChart>
		</ResponsiveContainer>
	);
}
